document.documentElement.classList.remove('no-js');

const shopRoot = () => window.Shopify?.routes?.root || '/';
const money = (cents, currency = document.documentElement.dataset.currency || 'USD') =>
  new Intl.NumberFormat(document.documentElement.lang, {
    style: 'currency',
    currency,
  }).format(cents / 100);

const escapeHTML = (value = '') =>
  String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  })[character]);

const safeURL = (value = '', allowExternal = false) => {
  try {
    const url = new URL(value, window.location.origin);
    if (!['http:', 'https:'].includes(url.protocol)) return '';
    if (!allowExternal && url.origin !== window.location.origin) return '';
    return escapeHTML(url.href);
  } catch {
    return '';
  }
};

let drawerTrigger = null;

function openDrawer(trigger = document.activeElement) {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;
  drawerTrigger = trigger;
  drawer.setAttribute('aria-hidden', 'false');
  document.body.classList.add('drawer-open');
  drawer.querySelector('[data-drawer-close]')?.focus();
}

function closeDrawer() {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;
  drawer.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('drawer-open');
  drawerTrigger?.focus();
}

function setGalleryIndex(gallery, requestedIndex) {
  const items = [...gallery.querySelectorAll('[data-gallery-item]')];
  if (!items.length) return;

  const nextIndex = ((requestedIndex % items.length) + items.length) % items.length;
  items.forEach((item, index) => {
    const isCurrent = index === nextIndex;
    item.hidden = !isCurrent;
    item.setAttribute('aria-hidden', String(!isCurrent));
  });

  gallery.dataset.currentIndex = String(nextIndex);
  const current = gallery.querySelector('[data-gallery-current]');
  if (current) current.textContent = String(nextIndex + 1);
}

function moveGallery(gallery, direction) {
  const currentIndex = Number.parseInt(gallery.dataset.currentIndex || '0', 10);
  setGalleryIndex(gallery, currentIndex + direction);
}

document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
  setGalleryIndex(gallery, Number.parseInt(gallery.dataset.currentIndex || '0', 10));

  let touchStartX = 0;
  let touchStartY = 0;
  gallery.addEventListener('touchstart', (event) => {
    const touch = event.changedTouches[0];
    touchStartX = touch.clientX;
    touchStartY = touch.clientY;
  }, { passive: true });

  gallery.addEventListener('touchend', (event) => {
    const touch = event.changedTouches[0];
    const distanceX = touch.clientX - touchStartX;
    const distanceY = touch.clientY - touchStartY;
    if (Math.abs(distanceX) < 45 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
    moveGallery(gallery, distanceX < 0 ? 1 : -1);
  }, { passive: true });
});

function cartItemMarkup(item, cart, labels) {
  const hasDiscount = item.original_line_price > item.final_line_price;
  const imageURL = safeURL(item.image, true);
  const itemURL = safeURL(item.url);
  const originalPrice = hasDiscount
    ? `<span class="compare-price"><span class="visually-hidden">${escapeHTML(labels.originalPrice)}: </span>${money(item.original_line_price, cart.currency)}</span>`
    : '';

  return `<div class="drawer-item">
    ${imageURL ? `<img src="${imageURL}" width="80" height="100" alt="">` : '<div></div>'}
    <div>
      <a href="${itemURL}">${escapeHTML(item.product_title)}</a>
      ${item.variant_title ? `<div class="muted">${escapeHTML(item.variant_title)}</div>` : ''}
      <div class="drawer-item__price">
        ${originalPrice}
        <strong><span class="visually-hidden">${escapeHTML(labels.currentPrice)}: </span>${money(item.final_line_price, cart.currency)}</strong>
      </div>
    </div>
    <button class="icon-button" data-remove-key="${escapeHTML(item.key)}" aria-label="${escapeHTML(labels.remove)}">×</button>
  </div>`;
}

async function refreshCart(open = false, trigger = document.activeElement) {
  const response = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Unable to load cart');
  const cart = await response.json();

  document.querySelectorAll('[data-cart-count]').forEach((element) => {
    element.textContent = cart.item_count;
  });

  const items = document.querySelector('[data-cart-items]');
  if (items) {
    const labels = {
      remove: items.dataset.removeLabel,
      originalPrice: items.dataset.originalPriceLabel,
      currentPrice: items.dataset.currentPriceLabel,
    };
    items.innerHTML = cart.items.length
      ? cart.items.map((item) => cartItemMarkup(item, cart, labels)).join('')
      : `<div class="empty-state">${escapeHTML(items.dataset.empty)}</div>`;
  }

  document.querySelectorAll('[data-cart-total]').forEach((element) => {
    element.textContent = money(cart.total_price, cart.currency);
  });

  const footer = document.querySelector('[data-cart-footer]');
  if (footer) footer.hidden = cart.items.length === 0;

  const savingsRow = document.querySelector('[data-cart-savings-row]');
  const savings = document.querySelector('[data-cart-savings]');
  if (savingsRow && savings) {
    savingsRow.hidden = cart.total_discount <= 0;
    savings.textContent = cart.total_discount > 0 ? money(cart.total_discount, cart.currency) : '';
  }

  if (open) openDrawer(trigger);
  return cart;
}

document.addEventListener('click', async (event) => {
  const galleryControl = event.target.closest('[data-gallery-direction]');
  if (galleryControl) {
    event.preventDefault();
    const gallery = galleryControl.closest('[data-product-gallery]');
    if (gallery) moveGallery(gallery, galleryControl.dataset.galleryDirection === 'next' ? 1 : -1);
  }

  const cartOpen = event.target.closest('[data-cart-open]');
  if (cartOpen) {
    event.preventDefault();
    await refreshCart(true, cartOpen);
  }

  if (event.target.closest('[data-drawer-close]')) closeDrawer();

  const remove = event.target.closest('[data-remove-key]');
  if (remove) {
    remove.disabled = true;
    const response = await fetch(`${shopRoot()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: remove.dataset.removeKey, quantity: 0 }),
    });
    if (response.ok) {
      const cart = await refreshCart();
      const status = document.querySelector('[data-cart-status]');
      if (status) status.textContent = cart.item_count ? '' : document.querySelector('[data-cart-items]')?.dataset.empty || '';
    } else {
      remove.disabled = false;
    }
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeDrawer();
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-product-form]');
  if (!form) return;

  event.preventDefault();
  const button = form.querySelector('[type=submit]');
  const status = form.querySelector('[data-product-status]');
  button.disabled = true;

  try {
    const response = await fetch(`${shopRoot()}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });
    if (!response.ok) throw new Error('Unable to add item');
    await refreshCart(true, button);
    if (status) status.textContent = button.dataset.addLabel;
  } catch {
    form.submit();
  } finally {
    button.disabled = false;
  }
});

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-variant-select]');
  if (!select) return;

  const option = select.selectedOptions[0];
  const available = option.dataset.available === 'true';
  const onSale = Boolean(option.dataset.comparePrice);

  document.querySelectorAll('[data-product-price]').forEach((element) => {
    element.textContent = option.dataset.price;
  });
  document.querySelectorAll('[data-product-compare-price]').forEach((element) => {
    element.textContent = option.dataset.comparePrice;
  });
  document.querySelectorAll('[data-product-compare-wrapper]').forEach((element) => {
    element.hidden = !onSale;
  });
  document.querySelectorAll('[data-product-price-label]').forEach((element) => {
    element.textContent = `${onSale ? element.dataset.saleLabel : element.dataset.regularLabel}: `;
  });
  document.querySelectorAll('[data-product-price-display]').forEach((element) => {
    element.classList.toggle('price-display--sale', onSale);
  });
  document.querySelectorAll('[data-sale-badge]').forEach((element) => {
    element.hidden = !onSale;
  });
  document.querySelectorAll('[data-sticky-id]').forEach((element) => {
    element.value = select.value;
  });
  document.querySelectorAll('[data-add-to-cart]').forEach((button) => {
    button.disabled = !available;
    button.textContent = available ? button.dataset.addLabel : button.dataset.soldLabel;
  });

  const gallery = document.querySelector('[data-product-gallery]');
  const mediaId = option.dataset.mediaId;
  if (gallery && mediaId) {
    const mediaItem = gallery.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
    if (mediaItem) setGalleryIndex(gallery, Number.parseInt(mediaItem.dataset.galleryIndex || '0', 10));
  }

  const url = new URL(window.location.href);
  url.searchParams.set('variant', select.value);
  window.history.replaceState({}, '', url);
});


const stickyAtc = document.querySelector('[data-sticky-atc]');
const mainAddButton = document.querySelector('.product-info [data-add-to-cart]');

if (stickyAtc && mainAddButton) {
  let stickyAtcFrame = null;

  const updateStickyAtc = () => {
    stickyAtcFrame = null;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const mainFormHasPassed = mainAddButton.getBoundingClientRect().top < 0;
    const shouldShowStickyAtc = isMobile && mainFormHasPassed;
    stickyAtc.hidden = !shouldShowStickyAtc;
    stickyAtc.classList.toggle('is-visible', shouldShowStickyAtc);
  };

  const requestStickyAtcUpdate = () => {
    if (stickyAtcFrame) return;
    stickyAtcFrame = window.requestAnimationFrame(updateStickyAtc);
  };

  stickyAtc.hidden = true;
  stickyAtc.classList.remove('is-visible');
  window.setTimeout(updateStickyAtc, 600);
  window.addEventListener('load', updateStickyAtc, { once: true });
  window.addEventListener('scroll', requestStickyAtcUpdate, { passive: true });
  window.addEventListener('resize', requestStickyAtcUpdate);
}